/***************************************************
 * src/RegistrationForm.js
 * Server-Side Only: We remove createFreeSurvey
 ***************************************************/
import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { GoogleLogin } from "@react-oauth/google";
import "./RegistrationForm.css";
import { AuthContext } from "./App";

function RegistrationForm({ isDarkMode }) {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  // Special Offer popup
  const [showPopup, setShowPopup] = useState(true);
  const handleClosePopup = () => setShowPopup(false);

  // Normal fields
  const [companyName, setCompanyName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Google flow
  const [isGoogleFlow, setIsGoogleFlow] = useState(false);
  const [googleFirstName, setGoogleFirstName] = useState("");
  const [googleLastName, setGoogleLastName] = useState("");
  const [googleEmail, setGoogleEmail] = useState("");
  const [googleToken, setGoogleToken] = useState(null);

  // reCAPTCHA
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  // Password criteria
  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });

  // Error display
  const [errorMessage, setErrorMessage] = useState("");
  const [fadeOut, setFadeOut] = useState(false);

  // Helpers
  const showError = (msg) => {
    setErrorMessage(msg);
    setFadeOut(false);
    setTimeout(() => setFadeOut(true), 15000);
    setTimeout(() => setErrorMessage(""), 20000);
  };

  const validatePassword = (pwd) => {
    setPasswordCriteria({
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      number: /[0-9]/.test(pwd),
      specialChar: /[@$!%*?&]/.test(pwd),
    });
  };

  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setPassword(val);
    validatePassword(val);
  };

  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const isRecaptchaDark = isDarkMode ? "dark" : "light";

  /***************************************************
   * Normal Registration
   ***************************************************/
  const handleNormalSubmit = async (e) => {
    e.preventDefault();

    const { length, uppercase, number, specialChar } = passwordCriteria;
    if (!length || !uppercase || !number || !specialChar) {
      showError("Password must meet all criteria (8 chars, uppercase, number, special char).");
      return;
    }
    if (password !== confirmPassword) {
      showError("Passwords do not match.");
      return;
    }
    if (!recaptchaToken) {
      showError("Please complete the reCAPTCHA challenge.");
      return;
    }

    // Gather fields
    const formData = {
      company_name: companyName,
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      recaptchaToken,
    };

    try {
      const response = await fetch("/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        const data = await response.json();
        const { token } = data;
        alert("Registration successful!");
        login(token);

        // No more createFreeSurvey call -> server does it

        // Reset fields
        setCompanyName("");
        setFirstName("");
        setLastName("");
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setErrorMessage("");
        setFadeOut(false);
        setPasswordCriteria({
          length: false,
          uppercase: false,
          number: false,
          specialChar: false,
        });
        setRecaptchaToken(null);

        navigate("/survey");
      } else {
        const errorData = await response.json();
        showError(`Error: ${errorData.error}`);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showError("An error occurred. Please try again.");
    }
  };

  /***************************************************
   * Google Registration Flow
   ***************************************************/
  const handleGoogleSuccess = (credentialResponse) => {
    const jwt = credentialResponse.credential;
    if (!jwt) {
      showError("Google returned no credential.");
      return;
    }
    const payload = parseJwt(jwt);
    if (!payload || !payload.email) {
      showError("Could not parse Google user info from token.");
      return;
    }

    setGoogleToken(jwt);
    setGoogleFirstName(payload.given_name || "");
    setGoogleLastName(payload.family_name || "");
    setGoogleEmail(payload.email);
    setIsGoogleFlow(true);
  };

  const handleGoogleError = () => {
    showError("Google Register Failed");
  };

  const finishGoogleRegistration = async (e) => {
    e.preventDefault();
    if (!recaptchaToken) {
      showError("Please complete the reCAPTCHA challenge.");
      return;
    }
    if (!companyName) {
      showError("Please enter a company name.");
      return;
    }
    if (!googleToken) {
      showError("No Google token found. Please click 'Sign in with Google' first.");
      return;
    }

    try {
      const resp = await fetch("/auth/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "register",
          token: googleToken,
          company_name: companyName,
          first_name: googleFirstName,
          last_name: googleLastName,
          email: googleEmail,
          recaptchaToken,
        }),
      });
      const data = await resp.json();
      if (resp.ok && data.token) {
        alert("Google Registration successful!");
        login(data.token);

        // No more createFreeSurvey call -> server does it

        navigate("/survey");
      } else {
        showError(data.error || "Google Registration failed. No token in response.");
      }
    } catch (err) {
      console.error("Google register error:", err);
      showError("An error occurred. Please try again.");
    }
  };

  // Decode ID token on client to get name/email
  function parseJwt(token) {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace("-", "+").replace("_", "/");
    try {
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("parseJwt error:", error);
      return null;
    }
  }

  return (
    <>
      <div className="registration-form-container">
        {/* Normal registration if not in Google flow */}
        {!isGoogleFlow && (
          <>
            <form onSubmit={handleNormalSubmit} className="registration-form">
              <h2>New Company Account Registration</h2>

              <label htmlFor="company-name">Company Name</label>
              <input
                type="text"
                id="company-name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Company Name"
                required
              />

              <h2>Admin User Information</h2>
              <label htmlFor="first-name">First Name</label>
              <input
                type="text"
                id="first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name"
                required
              />

              <label htmlFor="last-name">Last Name</label>
              <input
                type="text"
                id="last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name"
                required
              />

              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address"
                required
              />

              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={handlePasswordChange}
                placeholder="Enter a strong password"
                required
              />
              <div className="password-criteria">
                <p className={passwordCriteria.length ? "valid" : "invalid"}>
                  • At least 8 characters
                </p>
                <p className={passwordCriteria.uppercase ? "valid" : "invalid"}>
                  • At least one uppercase letter
                </p>
                <p className={passwordCriteria.number ? "valid" : "invalid"}>
                  • At least one number
                </p>
                <p className={passwordCriteria.specialChar ? "valid" : "invalid"}>
                  • At least one special character (@, $, !, %, *, ?, &)
                </p>
              </div>

              <label htmlFor="confirm-password">Confirm Password</label>
              <input
                type="password"
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm Password"
                required
              />

              {errorMessage && (
                <p className={`error-message ${fadeOut ? "fade" : ""}`}>
                  {errorMessage}
                </p>
              )}

              <div className="recaptcha-container">
                <ReCAPTCHA
                  key={isDarkMode ? "dark-recaptcha" : "light-recaptcha"}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={onRecaptchaChange}
                  theme={isRecaptchaDark}
                />
              </div>

              <button type="submit" className="submit-btn">
                Register
              </button>
            </form>

            {/* Google */}
            <div style={{ marginTop: "1rem" }}>
              <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} />
            </div>
          </>
        )}

        {/* Google step 2 */}
        {isGoogleFlow && (
          <form onSubmit={finishGoogleRegistration} className="registration-form">
            <h2>Google Registration</h2>
            <p>
              We have auto-filled your name and email from Google. Please confirm and
              enter your Company Name.
            </p>

            <label>First Name (from Google)</label>
            <input type="text" value={googleFirstName} disabled />

            <label>Last Name (from Google)</label>
            <input type="text" value={googleLastName} disabled />

            <label>Email (from Google)</label>
            <input type="email" value={googleEmail} disabled />

            <label>Company Name</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Company Name"
              required
            />

            {errorMessage && (
              <p className={`error-message ${fadeOut ? "fade" : ""}`}>
                {errorMessage}
              </p>
            )}

            <div className="recaptcha-container">
              <ReCAPTCHA
                key={isDarkMode ? "dark-recaptcha" : "light-recaptcha"}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={onRecaptchaChange}
                theme={isRecaptchaDark}
              />
            </div>

            <button type="submit" className="submit-btn">
              Finish Google Registration
            </button>
          </form>
        )}
      </div>

      {/* Special Offer popup */}
      {showPopup && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2>Special Offer</h2>
            <p>
              For a limited time, receive <strong>1 FREE Feedback Group</strong> for up to{" "}
              <strong>500 responses</strong> upon registration!
            </p>
            <button onClick={handleClosePopup} className="logout-btn">
              OK, got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default RegistrationForm;
